/**
 * The hold a merchandise draft estimate goes into when its Promo Office quote
 * changes after pricing (brief §5 slice A, J4). Says what changed in money
 * and what to do about it: re-price or withdraw. Nothing re-prices silently.
 *
 * There is one reading of the hold. An earlier version branched on `sentAt`
 * for a second one — "this estimate was sent to the customer" — but slice A
 * has no send, so that branch could never be taken and its copy described a
 * distinction that does not exist.
 *
 * The surviving copy states the actual choice and nothing more. It must not
 * promise that re-pricing unlocks sending: it does not, and a CSR who
 * re-prices to unlock sending finds nothing there. The "sending arrives with
 * multi-line estimates" explanation belongs once, in the price card's stated
 * note.
 */
import { TriangleAlert, Loader2 } from "lucide-react";
import type { MerchandiseDraftEstimate } from "@shared-core/data/merchandise-types";
import { formatQuotedPrice } from "@shared-core/domain/formatters";
import { Button } from "../../primitives/button";
import { Card } from "../../primitives/card";

//: No "send": slice A has none, and a busy state for an action that cannot be
//: taken would keep a dead branch alive in every consumer of this type.
export type DraftEstimateBusyAction = "reprice" | "withdraw" | "unlink" | null;

export function DraftEstimateCostChangedBanner({
  request,
  busy,
  onReprice,
  onWithdraw,
}: {
  request: MerchandiseDraftEstimate;
  busy: DraftEstimateBusyAction;
  onReprice: () => void;
  onWithdraw: () => void;
}) {
  const change = request.costChange;
  if (request.state !== "cost_changed" || !change) return null;
  const priced = request.pricing;

  return (
    <Card
      className="p-4 border-[color:var(--color-warning)]"
      role="status"
      aria-label="Quote changed"
    >
      <div className="flex items-start gap-3">
        <TriangleAlert className="size-5 shrink-0 mt-0.5 text-[color:var(--color-warning)]" />
        <div className="min-w-0 flex-1 space-y-3">
          <div>
            <p className="font-medium">Quote changed</p>
            <p className="text-sm text-muted-foreground mt-0.5">
              {change.reason}
            </p>
          </div>

          <dl className="grid gap-x-8 gap-y-1 text-sm sm:grid-cols-2 max-w-2xl">
            {priced && (
              <div>
                <dt className="text-xs text-muted-foreground">
                  Quoted price when priced
                </dt>
                <dd className="tabular-nums">
                  {formatQuotedPrice(priced.ourPricePence)}
                </dd>
              </div>
            )}
            {change.currentQuotedPricePence != null && (
              <div>
                <dt className="text-xs text-muted-foreground">
                  Quoted price now
                </dt>
                <dd className="tabular-nums font-medium">
                  {formatQuotedPrice(change.currentQuotedPricePence)}
                </dd>
              </div>
            )}
            <div>
              <dt className="text-xs text-muted-foreground">
                Buy price when priced
              </dt>
              <dd className="tabular-nums">
                {formatQuotedPrice(change.previousBuyTotalPence)}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-muted-foreground">Buy price now</dt>
              <dd className="tabular-nums font-medium">
                {formatQuotedPrice(change.currentBuyTotalPence)}
              </dd>
            </div>
          </dl>

          <p className="text-sm">
            {/* The ticket link is shown whenever there is one — the CSR is
                notified on the *first* hold and not again while it stays held,
                so this page is the only place a second move is visible. */}
            {change.csrTicket
              ? `A ticket has been raised to you (${change.csrTicket.id}). `
              : ""}
            Re-price to take the merchandise team&apos;s latest quote, or
            withdraw the request.
          </p>

          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" disabled={busy !== null} onClick={onReprice}>
              {busy === "reprice" && (
                <Loader2 className="size-3.5 animate-spin" />
              )}
              Re-price from the latest quote
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={busy !== null}
              onClick={onWithdraw}
            >
              {busy === "withdraw" && (
                <Loader2 className="size-3.5 animate-spin" />
              )}
              Withdraw…
            </Button>
            <span className="text-xs text-muted-foreground">
              Re-pricing takes the quote as it stands today.
            </span>
          </div>
        </div>
      </div>
    </Card>
  );
}
